/**
 * English messages. This file is the SHAPE of the message catalogue — every
 * other locale is typed against it, so a missing key is a compile error rather
 * than an empty string discovered by a client.
 *
 * Keys are flat and dotted. Placeholders are `{name}`.
 */
export const en = {
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.remove': 'Remove',
  'common.rename': 'Rename',
  'common.expand': 'Open',
  'common.collapse': 'Close',
  'common.none': 'None',

  'nav.trips': 'Trips',
  'nav.signOut': 'Sign out',
  'nav.settings': 'Settings',
  'nav.language': 'Language',
  'nav.account': 'Your account',

  'account.title': 'Your account',
  'account.signedInAs': 'Signed in as {email}.',
  'account.password.title': 'Change your password',
  'account.password.current': 'Current password',
  'account.password.new': 'New password',
  'account.password.confirm': 'Type the new password again',
  'account.password.submit': 'Change password',
  'account.password.lengthHint': 'At least {min} characters.',
  'account.password.changed':
    'Your password has been changed. Anywhere else you were signed in has been signed out.',
  'account.password.mismatch': 'These two do not match.',
  'account.password.tooShort': 'Too short — at least {min} characters.',
  'account.password.sameAsCurrent': 'This is already your password.',
  'account.password.wrongCurrent': 'That is not your current password.',
  'account.password.current.guidance':
    'Asked for even though you are already signed in. It is what stops somebody who has got hold of this browser from locking you out of your own account.',
  'account.password.new.guidance':
    'Length is what makes a password hard to guess — a phrase of four or five ordinary words beats a short one with symbols in it. Changing it here signs out every other phone and laptop you were signed in on.',

  'login.title': 'Sign in',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.failed': 'That email and password do not match.',

  // Guidance. Every `*.guidance` key below is scaffolding — see the note on
  // `guidanceLevel` in contracts/auth.ts. Retiring them is a deletion of every
  // key matching `.guidance` from this file and pt-BR.ts, and nothing else.
  'guidance.label': 'Field explanations',
  'guidance.full': 'Explain fields',
  'guidance.brief': 'Short hints',
  'guidance.off': 'No hints',

  'timezone.searchPlaceholder': 'Search for a city — Paris, London, São Paulo…',
  'timezone.suggestionsLabel': 'Common destinations, or search for a city above.',
  'timezone.noMatches': 'No city by that name. Try the nearest large one.',
  'timezone.listLabel': 'Cities',
  'timezone.confirm': 'It is {time} in {city} right now. If that looks wrong, the city is wrong.',
  'timezone.sameAsTrip': 'Same as the trip — {city}',
  'timezone.elsewhere': 'Somewhere else',
  'timezone.landsSame': 'Same place — {city}',
  'timezone.landsElsewhere': 'It lands somewhere else',
  'timezone.flightReadsAs': 'Leaves {depTime} in {depCity}, arrives {arrTime} in {arrCity}.',
  'timezone.duration': 'About {hours}h in the air.',
  'timezone.readsAs': '{time} in {city}. The traveller’s page will show {tripTime}, because it reads times on {tripCity} time.',

  'trips.title': 'Trips',
  'trips.empty': 'No trips yet.',
  'trips.new': 'New trip',
  'trips.field.title': 'Trip name',
  'trips.field.title.guidance': 'What you will recognise this trip by in the list. The traveller sees it as the heading of their own page, so name it the way they would: “Longhi family — Paris, July”, not “Job 214”.',
  'trips.field.startDate': 'Start date',
  'trips.field.startDate.guidance': 'The first day of the trip. One day is created for every date between this and the end date, ready for you to fill in. Changing it later adds the missing days and never deletes one you have already written on.',
  'trips.field.endDate': 'End date',
  'trips.field.endDate.guidance': 'The last day of the trip. Passport expiry is checked against this date — including the six-months-beyond-return rule that many countries apply — so it is worth getting right before you add documents.',
  'trips.field.timezone': 'Where the trip happens',
  'trips.field.timezone.guidance': 'The city whose clock the traveller is on. Every booking time you enter is shown to them on this clock, unless you set a different city on that one booking — which is what you do for a flight that crosses zones.',
  'trips.field.status': 'Status',
  'trips.status.planning': 'Planning',
  'trips.status.ticketed': 'Ticketed',
  'trips.status.travelling': 'Travelling',
  'trips.status.complete': 'Complete',
  'trips.dates': '{start} to {end}',

  'participants.title': 'Travellers',
  'participants.empty': 'Nobody added yet.',
  'participants.add': 'Add traveller',
  'participants.field.displayName': 'Name',
  'participants.field.displayName.guidance': 'How this person is greeted when they open their link. They cannot change it, so spell it the way they would — first name alone is usually right.',
  'participants.field.role': 'Role',
  'participants.field.role.guidance': 'An advisor sees everything on the trip. A traveller sees the itinerary and their own documents, never another traveller’s.',
  'participants.field.email': 'Email',
  'participants.field.email.guidance': 'Kept so you can reach them. It is not a login — nobody creates an account here — and it is never shown to the other travellers.',
  'participants.field.phone': 'Phone',
  'participants.field.phone.guidance': 'The number you would call if a flight moved. Stored for you; it does not appear on the traveller’s page.',
  'participants.role.advisor': 'Advisor',
  'participants.role.traveller': 'Traveller',
  'participants.nameNote': 'You set this name. The person opening the link cannot change it.',
  'participants.removeConfirm': 'Remove {name} from this trip?',

  'share.title': 'Links',
  'share.empty': 'No links yet.',
  'share.create': 'Create link',
  'share.createFor': 'Create link for {name}',
  'share.unbound': 'Not named',
  'share.boundTo': 'For {name}',
  'share.revoke': 'Revoke',
  'share.revoked': 'Revoked',
  'share.revokeConfirm': 'Revoke this link? Anyone holding it loses access immediately.',

  'share.nameIt': 'Name it',
  'share.namePlaceholder': 'grupo do WhatsApp, cópia do Carlos…',
  'share.nameHint': 'Only you see this. It is how you tell one link from another.',
  'share.allRevoked': 'Every link has been revoked. Create a new one to share this trip.',
  'share.showRevoked': 'Show {count} revoked',
  'share.hideRevoked': 'Hide revoked',

  'sharePage.greeting': 'Hello, {name}',
  'sharePage.anonymous': 'Your trip',
  'sharePage.preparedBy': 'Prepared by {agency}',
  'sharePage.colophon': 'Itinerary prepared by {agency} ·',
  'sharePage.print': 'Print / Save as PDF',
  'sharePage.notFound': 'This link is not valid. Ask your advisor for a new one.',

  'plan.title': 'Itinerary',
  'progress.dayOf': 'Day {day} of {total}',
  'progress.startsToday': 'Today',
  'progress.startsIn': 'In {days} days',
  'progress.underway': 'Under way',
  'progress.next': 'Next:',
  'progress.nothingScheduled': 'Nothing else with a time on it.',
  'progress.jumpToToday': 'Go to today',

  // What changed since the traveller last looked. Two headline keys rather than
  // one with a `{count}` placeholder: "1 things have changed" is the kind of
  // small wrongness that makes a page read as software rather than as a document
  // somebody prepared, and Portuguese needs a different verb form anyway.
  'updates.oneChanged': '1 thing has changed since you last looked',
  'updates.manyChanged': '{count} things have changed since you last looked',
  // The honest small print. It states both limits — per-device, and blind to
  // removals — because a traveller who trusts this to be complete is worse off
  // than one who never had it.
  'updates.limits': 'Counted on this device only, and it cannot tell you about anything that was removed.',
  'updates.show': 'Show me',
  'updates.dismiss': 'Got it',
  'updates.added': 'New',
  'updates.edited': 'Changed',

  // The offline copy, and the promise that it never pretends to be live.
  // `storedCopy` is the only one of these that prints: a paper itinerary that
  // came off a saved copy should carry the date it was saved, because that is
  // the sheet somebody ends up holding at a desk. The rest is about a phone's
  // connection this minute and means nothing once it is on paper.
  'offline.storedCopy': 'This is the copy saved on this device, from {when}.',
  'offline.mayHaveChanged': 'You are offline, so anything changed since then is not here yet. Open it again when you have signal.',
  'offline.notStored': 'You are offline, and this itinerary is not saved on this device yet. Open the link once with a connection and it will open without one afterwards.',
  'offline.needsConnection': 'This needs a connection. Try again when you have signal.',

  'plan.emergency': 'Emergency',
  'plan.unscheduled': 'Not yet scheduled',
  'plan.empty': 'Nothing here yet.',
  'plan.confirmation': 'Confirmation',
  'plan.openMap': 'Map',
  'plan.call': 'Call',
  'plan.website': 'Website',
  'plan.localTimeIn': 'local time in {place}',
  'plan.noDay': 'No day',

  'reservation.kind.flight': 'Flight',
  'reservation.kind.train': 'Train',
  'reservation.kind.hotel': 'Hotel',
  'reservation.kind.car': 'Car',
  'reservation.kind.transfer': 'Transfer',
  'reservation.kind.restaurant': 'Restaurant',
  'reservation.kind.activity': 'Activity',
  'reservation.kind.other': 'Other',

  'day.notes': 'Notes for this day',
  'day.notesHint': 'Markdown. Links and phone numbers become tappable.',
  'day.notes.guidance': 'Whatever the traveller should read about this day, in your words. It appears above that day’s bookings on their page and on the printed copy.',

  'places.title': 'Places',
  'places.add': 'Add place',
  'places.field.name': 'Name',
  'places.field.name.guidance': 'What the place is called. Shown to the traveller and used as the label on its map link.',
  'places.field.address': 'Address',
  'places.field.address.guidance': 'The address as you would write it. Used for the map link when there are no coordinates.',
  'places.field.addressLocal': 'Address in the local language',
  'places.field.addressLocalHint': 'What you would show a driver.',
  'places.field.addressLocal.guidance': 'The same address in the local language and script. Shown on its own line so it can be held up to a driver who does not read Portuguese — on the printed page this is the line that does the most work.',
  'places.field.lat': 'Latitude',
  'places.field.lat.guidance': 'Optional. With coordinates the map link goes to the door rather than to the street. Fill this and longitude together — one on its own is ignored.',
  'places.field.lng': 'Longitude',
  'places.field.lng.guidance': 'The other half of the coordinates. Paste both from the map, or leave both empty and let the address do the work.',
  'places.field.website': 'Website',
  'places.field.website.guidance': 'Opens in a new tab from the traveller’s page. Only http and https addresses are shown — anything else is dropped rather than displayed.',
  'places.field.phone': 'Phone',
  'places.field.phone.guidance': 'Becomes a tap-to-call link on the traveller’s phone. Include the country code, or it will not connect from abroad.',
  'places.field.notes': 'Notes',
  'places.field.day': 'Day',

  'reservations.title': 'Bookings',
  'reservations.add': 'Add booking',
  'reservations.field.kind': 'Type',
  'reservations.field.kind.guidance': 'Sets the label the traveller sees on this booking. It changes nothing else — every type takes the same fields.',
  'reservations.field.title': 'Description',
  'reservations.field.title.guidance': 'What this booking is, in one line: “LATAM 8064 GRU → CDG” or “Hotel du Louvre, 3 nights”. It is the heading the traveller reads.',
  'reservations.field.confirmationRef': 'Confirmation number',
  'reservations.field.confirmationRef.guidance': 'The number someone reads out at a desk. Shown as plain text and never turned into a link, so a policy number is never mistaken for a phone number.',
  'reservations.field.startsAt': 'Starts',
  'reservations.field.startsAt.guidance': 'When it starts, on the clock of the city named below. A flight leaving São Paulo starts at 14:40 in São Paulo — not 14:40 in Paris.',
  'reservations.field.endsAt': 'Ends',
  'reservations.field.timezone': 'Times above are on the clock in',
  'reservations.field.timezone.guidance': 'Which city’s clock you are reading the times from. Almost everything happens where the trip is, so the trip’s city is the usual answer. A flight that crosses zones is the exception, and this is where you say so — the traveller then sees the departure labelled with the city it departs from.',

  'trips.field.emergencyInfo': 'Emergency information',
  'trips.field.emergencyInfoHint': 'Consulate, insurance line, your own mobile. Shown at the top of the traveller’s page.',
  'trips.field.emergencyInfo.guidance': 'Sits above the itinerary on the traveller’s page and prints at the top of the paper copy, so it is findable by someone who is not calm.',

  'money.title': 'Deadlines and money',
  'money.totals.title': 'Running total',
  'money.totals.empty': 'No costs yet.',
  'money.deadlines.title': 'What needs you, by when',
  'money.deadlines.empty': 'No deadlines yet.',
  'money.deadlines.add': 'Add deadline',
  'money.deadlines.markDone': 'Mark done',
  'money.deadlines.reopen': 'Reopen',
  'money.deadlines.field.label': 'What',
  'money.deadlines.field.label.guidance': 'What has to happen — “pay the balance”, “send passport details”. Deadlines are yours alone; the traveller’s page does not show them.',
  'money.deadlines.field.dueAt': 'By when',
  'money.deadlines.field.dueAt.guidance': 'The date it stops being early and starts being late. Anything past its date is marked as missed on this card.',
  'money.deadlines.field.owner': 'Whose move',
  'money.deadlines.field.owner.guidance': 'Whether this is waiting on you or on the traveller. Nothing happens automatically — it is here so you can see at a glance which list is yours.',
  'money.deadlines.owner.advisor': 'Advisor',
  'money.deadlines.owner.traveller': 'Traveller',
  'money.deadlines.status.open': 'Open',
  'money.deadlines.status.done': 'Done',
  'money.deadlines.status.missed': 'Missed',
  'money.costLines.title': 'Costs',
  'money.costLines.empty': 'No costs yet.',
  'money.costLines.add': 'Add cost',
  'money.costLines.field.label': 'What',
  'money.costLines.field.label.guidance': 'What the money is for. It appears in the running total on this card, which only you see.',
  'money.costLines.field.amount': 'Amount',
  'money.costLines.field.amount.guidance': 'The amount in the currency beside it. Totals are kept per currency and never converted — adding euros to reais at today’s rate would produce a number that is wrong by the time anyone pays it.',
  'money.costLines.field.currency': 'Currency',
  'money.costLines.field.currency.guidance': 'Three letters: BRL, EUR, USD. Each currency gets its own line in the total.',
  'money.costLines.field.dueAt': 'Due',
  'money.costLines.status.quoted': 'Quoted',
  'money.costLines.status.committed': 'Committed',
  'money.costLines.status.paid': 'Paid',
  'money.removeConfirm': 'Remove {label}?',
  // Build unit 7.16 — travel document details. Fields, never images.
  'travelDocuments.title': 'Travel documents',
  'travelDocuments.empty': 'No document on file.',
  'travelDocuments.add': 'Add document',
  'travelDocuments.edit': 'Edit',
  'travelDocuments.noImageNote': 'Ana types these from the document — there is no photo upload here. A photo sent by WhatsApp never needs to be stored.',
  'travelDocuments.field.kind': 'Document type',
  'travelDocuments.field.kind.guidance': 'What you are recording. Passports are checked against the trip’s dates; ID cards and visas are stored without that check.',
  'travelDocuments.kind.passport': 'Passport',
  'travelDocuments.kind.id_card': 'ID card',
  'travelDocuments.kind.visa': 'Visa',
  'travelDocuments.field.fullNameAsPrinted': 'Name as printed',
  'travelDocuments.field.fullNameAsPrintedHint': 'Exactly as it appears on the document — airlines match this character for character.',
  'travelDocuments.field.fullNameAsPrinted.guidance': 'Copy it exactly, accents and all. If it differs from the name you gave this traveller you get a warning here — which is better than the airline finding the difference at check-in.',
  'travelDocuments.field.documentNumber': 'Document number',
  'travelDocuments.field.documentNumber.guidance': 'The number printed on the document. You type it; there is no photo upload here, so a scan sent to you by WhatsApp never has to be stored anywhere.',
  'travelDocuments.field.nationality': 'Nationality',
  'travelDocuments.field.nationality.guidance': 'The nationality shown on the document, which is not always the country that issued it. Two letters.',
  'travelDocuments.field.issuingCountry': 'Issuing country',
  'travelDocuments.field.issuingCountry.guidance': 'The country that issued the document. Two letters.',
  'travelDocuments.field.dateOfBirth': 'Date of birth',
  'travelDocuments.field.dateOfBirth.guidance': 'As printed on the document, for the booking forms that ask for it.',
  'travelDocuments.field.expiresOn': 'Expiry date',
  'travelDocuments.field.expiresOn.guidance': 'Compared against the trip’s end date. You are warned twice: if it expires during the trip, and if it expires within six months of the return — many countries refuse entry on the second alone.',
  'travelDocuments.field.purgeAfter': 'Kept until',
  'travelDocuments.countryHint': 'Two-letter code, e.g. BR or US.',
  'travelDocuments.removeConfirm': 'Remove this document? The details cannot be recovered.',
  'travelDocuments.warning.expired_before_return': 'Expires {expiresOn}, before the trip returns on {returnDate}.',
  'travelDocuments.warning.expires_within_six_months': 'Expires {expiresOn} — many destinations require six months’ validity beyond the return date ({returnDate}).',
  'travelDocuments.warning.name_mismatch': 'Printed as “{printedName}”, saved here as “{displayName}”.',
  'consent.title': 'Health, dietary & accessibility',
  'consent.hint': 'Sensitive under Brazilian data-protection law. Recording one requires the traveller’s consent to the exact wording below — not a box ticked in the terms.',
  'consent.empty': 'Nothing recorded.',
  'consent.purpose.health': 'Health',
  'consent.purpose.dietary': 'Dietary',
  'consent.purpose.accessibility': 'Accessibility',
  'consent.add': 'Record something',
  'consent.edit': 'Update',
  'consent.field.purpose': 'Category',
  'consent.field.purpose.guidance': 'Picks the consent wording shown below — which is the exact wording the traveller has to agree to, so choose the category that matches what you are about to write.',
  'consent.field.detail': 'What are you recording?',
  'consent.field.detailHint': 'Example: “Type 1 diabetic, carries insulin.”',
  'consent.field.detail.guidance': 'What you were told, in as few words as do the job. This is sensitive information under Brazilian data-protection law: it saves only with a recorded consent, and withdrawing that consent makes it unreadable everywhere in the app immediately.',
  'consent.preview': 'This is the consent you are recording:',
  'consent.confirm': 'I have explained this to the traveller and obtained their consent to the wording above',
  'consent.record': 'Record consent and save',
  'consent.grantedOn': 'Consented {date}',
  'consent.withdraw': 'Withdraw',
  'consent.withdrawConfirm': 'Withdraw this consent? The recorded information becomes unreadable immediately, everywhere in the app.',
  'consent.withdrawn': 'Withdrawn {date}',
  'consent.noteLabel': 'Recorded note',
  'consent.noteUnreadable': 'Withdrawn — no longer readable. Record a fresh consent to view or change it.',
  'consent.statement.health': 'I consent to health information about {participant} being recorded by {agency} for the purpose of planning and supporting this trip, and shared with those who need it to keep {participant} safe while travelling.',
  'consent.statement.dietary': 'I consent to dietary information about {participant} being recorded by {agency} for the purpose of planning and supporting this trip, and shared with venues and suppliers as needed.',
  'consent.statement.accessibility': 'I consent to accessibility requirements for {participant} being recorded by {agency} for the purpose of planning and supporting this trip, and shared with venues and suppliers as needed.',
  'files.title': 'Documents',
  'files.empty': 'No documents yet.',
  'files.upload': 'Upload document',
  'files.uploading': 'Uploading…',
  'files.field.file': 'File',
  'files.field.file.guidance': 'Up to 20 MB. Vouchers, tickets, insurance certificates — not passport photographs, which belong in the document fields above where they are typed rather than stored.',
  'files.field.category': 'Category',
  'files.field.category.guidance': 'How the file is labelled on the traveller’s page. Nothing else depends on it.',
  'files.field.sensitivity': 'Sensitivity',
  'files.field.sensitivity.guidance': 'A shareable file downloads with one tap. A sensitive file asks for the unlock code first, and that code never travels in the link — so a forwarded link opens nothing.',
  'files.field.unlockCode': 'Unlock code',
  'files.field.unlockCodeHint': 'Share this with the traveller by phone or message, never by email.',
  'files.field.unlockCode.guidance': 'The code the traveller types to open this file. Send it by phone or message: emailing it puts the file and its key in the same inbox, which is the same as having no code at all.',
  'files.category.voucher': 'Voucher',
  'files.category.insurance': 'Insurance certificate',
  'files.category.ticket': 'Ticket',
  'files.category.other': 'Other',
  'files.sensitivity.sensitive': 'Sensitive — needs a code',
  'files.sensitivity.shareable': 'Shareable — no code needed',
  'files.sensitiveBadge': 'Locked',
  'files.download': 'Download',
  'files.downloading': 'Downloading…',
  'files.enterCode': 'Enter code',
  'files.unlockPrompt': 'Enter the unlock code to download this file.',
  'files.unlockSubmit': 'Unlock and download',
  'files.unlockFailed': 'Wrong code, or this file is no longer available.',
  'files.removeConfirm': 'Remove {name}? This cannot be undone.',
  'files.retention': 'Kept until {date}',
  'files.tooLarge': 'That file is larger than 20 MB.',
  'files.badType': 'That file type is not supported.',

  'places.removeConfirm': 'Remove {name} from this trip? The traveller stops seeing it immediately.',
  'reservations.removeConfirm': 'Remove {name}? The traveller stops seeing it immediately.',

  'savedPlaces.title': 'My places',
  'savedPlaces.hint': 'Places you recommend, kept between trips. Adding one to a trip copies it, so editing it here never changes an itinerary somebody already has.',
  'savedPlaces.empty': 'Nothing kept yet. Add a place below, or press “Keep” beside any place on this trip.',
  'savedPlaces.add': 'Keep a place',
  'savedPlaces.addToTrip': 'Add to trip',
  'savedPlaces.keep': 'Keep',
  'savedPlaces.here': 'here',
  'savedPlaces.search': 'Search your places',
  'savedPlaces.removeConfirm': 'Remove {name} from your places? Trips that already use it are not affected.',
  'savedPlaces.addFrom': 'From my places',
  'savedPlaces.noMatches': 'Nothing matches. Try fewer words, or clear the filters.',
  'savedPlaces.filter.destination': 'Destination',
  'savedPlaces.filter.allDestinations': 'Everywhere',
  'savedPlaces.filter.kind': 'Kind',
  'savedPlaces.filter.allKinds': 'Any kind',
  'savedPlaces.field.kind': 'What kind of place',
  'savedPlaces.field.kind.guidance': 'Narrows the list once you have a lot of these. Coarse on purpose — a category fine enough to be interesting is one nobody fills in the same way twice, and “other” is a real answer.',
  'savedPlaces.kind.restaurant': 'Restaurant',
  'savedPlaces.kind.hotel': 'Hotel',
  'savedPlaces.kind.cafe': 'Café',
  'savedPlaces.kind.bar': 'Bar',
  'savedPlaces.kind.museum': 'Museum',
  'savedPlaces.kind.shop': 'Shop',
  'savedPlaces.kind.activity': 'Activity',
  'savedPlaces.kind.other': 'Other',
  'savedPlaces.field.destination': 'Where it is',
  'savedPlaces.field.destination.guidance': 'How you group it — “Paris”, “Toscana”, “Lisboa”. Places matching the city of the trip you are building are listed first; the rest stay one search away, so a rough answer costs nothing.',
  'savedPlaces.field.notes.guidance': 'Why you recommend it, in your words. It is copied onto the trip along with everything else, so write it as the traveller should read it.',

  'snippets.title': 'My texts',
  'snippets.hint': 'The paragraphs you write on every trip. Inserting one makes a copy, so improving it here never rewrites an itinerary somebody already has.',
  'snippets.empty': 'Nothing kept yet. Start with the one you retype most — how to get from the airport, how tipping works, what to do if a passport goes missing.',
  'snippets.add': 'Keep a text',
  'snippets.addFrom': 'From my texts',
  'snippets.insertHint': 'The text is copied into this note. What is already written stays, and you can edit the copy without touching the original.',
  'snippets.search': 'Search your texts',
  'snippets.noMatches': 'Nothing matches. Try fewer words, or clear the filters.',
  'snippets.removeConfirm': 'Remove “{title}” from your texts? Trips it was already inserted into keep their copy.',
  // Not "Edit": the itinerary's own edit controls are on the same screen, and
  // two controls on one screen never share a label. Same for the two filters
  // below, which would otherwise read identically to the places library's.
  'snippets.edit': 'Edit text',
  'snippets.here': 'here',
  'snippets.anywhere': 'Anywhere',
  'snippets.filter.destination': 'Filter texts by destination',
  'snippets.filter.allDestinations': 'Everywhere',
  'snippets.filter.category': 'Filter texts by kind',
  'snippets.filter.allCategories': 'Any kind',
  'snippets.field.title': 'What to call it',
  'snippets.field.title.guidance': 'How you will find it again — “CDG to the hotel”, “Tipping in Italy”. Only you ever see this; the traveller reads the text itself.',
  'snippets.field.category': 'What kind of text',
  'snippets.field.category.guidance': 'Narrows the list once you have a lot of these. Coarse on purpose, and “other” is a real answer.',
  'snippets.field.destination': 'Where it applies',
  'snippets.field.destination.guidance': 'Leave it empty for something true of every trip — losing a passport, travel insurance, what to do at the airport. Fill it in for “Paris” or “Toscana” and it moves to the top of the list when you are building a trip there.',
  'snippets.field.body': 'The text',
  'snippets.field.body.guidance': 'Write it as the traveller should read it, because that is exactly what they get. Same formatting as any note: **bold**, lists, links.',
  'snippets.category.arrival': 'Arriving',
  'snippets.category.transport': 'Getting around',
  'snippets.category.practical': 'Practical tips',
  'snippets.category.dining': 'Eating out',
  'snippets.category.safety': 'Safety',
  'snippets.category.packing': 'What to pack',
  'snippets.category.other': 'Other',

  'trips.duplicate': 'Duplicate',
  'trips.duplicateTitle': 'Duplicate “{title}”',
  'trips.duplicateHint': 'Copies the days, places, bookings and emergency block. It does not copy the travellers, their documents, their links, the money, or the confirmation numbers — those belong to the trip that was actually sold, not to the plan.',
  'trips.duplicate.title.guidance': 'The copy needs its own name or you will not tell them apart in the list a month from now.',
  'trips.duplicate.startDate.guidance': 'When the new trip begins. Every day and every booking moves with it, keeping the time of day it had — a 09:00 museum stays 09:00 even if the copy crosses a daylight-saving change. Leave it empty to keep the original dates.',

  'templates.title': 'Templates',
  'templates.empty': 'No templates yet. Keep a trip you would build again as one and it appears here.',
  'templates.hint': 'A trip you keep as a starting point. Using one makes a new trip with the same days, notes and bookings, moved onto the dates you give it — and nothing that belonged to the last client.',
  // Not "Save as template": on this screen it would sit above the new-trip
  // form's own "Save", and one word matching two controls is a mis-click for
  // Ana before it is an ambiguous selector for a test.
  'templates.mark': 'Keep as template',
  'templates.unmark': 'Remove from templates',
  'templates.use': 'Use template',
  'templates.useTitle': 'New trip from “{title}”',
  'templates.useHint': 'The days, notes, places and bookings come across, moved onto the new dates. The travellers, their documents, their links, the money and the confirmation numbers do not — they belonged to the trip that was actually sold.',
  'templates.create': 'Create the trip',
  'templates.lengthOne': '1 day',
  'templates.length': '{count} days',
  'templates.use.title.guidance': 'The client’s trip, in the client’s words. The template’s name is how you find it in this list; it is not what the traveller should read at the top of their page.',
  'templates.use.startDate.guidance': 'The first day of the new trip. The template’s days move onto it in order, keeping their spacing and the time of day of every booking — a 09:00 museum stays 09:00 even if the new dates cross a daylight-saving change.',

  'copyDay.open': 'Copy a day',
  'copyDay.hint': 'Days from your other trips. Everything on the day is added to this one — what is already here stays.',
  'copyDay.empty': 'Nothing to copy yet. Days from your other trips appear here once they have something on them.',
  'copyDay.search': 'Search your trips',
  'copyDay.places': '{count} places',
  'copyDay.reservations': '{count} bookings',
  'copyDay.withNotes': 'with notes',

  // --- Reading a confirmation email ----------------------------------------
  'confirmations.title': 'Paste a confirmation',
  'confirmations.hint': 'Paste the email a hotel or an airline sent you and the details come back as fields. Nothing is added to the trip until you have read them and pressed Add.',
  'confirmations.field.text': 'The email',
  'confirmations.field.text.guidance': 'The whole thing, including the lines above the message. Dates that could be read two ways are settled against this trip’s own dates, so a June confirmation pasted onto a July trip is the one case worth checking twice.',
  'confirmations.placeholder': 'Paste the confirmation email here…',
  'confirmations.read': 'Read it',
  'confirmations.reading': 'Reading…',
  'confirmations.unavailable': 'Reading confirmations is not switched on for this server. Everything else works as usual — type the booking in by hand.',
  'confirmations.failed': 'The reader could not be reached. Try again in a moment, or type the booking in by hand.',
  'confirmations.nothingFound': 'Nothing here looks like a booking. Paste the whole email rather than one line of it.',
  'confirmations.foundOne': 'One booking read.',
  'confirmations.found': '{count} bookings read.',
  'confirmations.checkHint': 'Read these against the email before you add them. The booking reference above all — one wrong character is invisible here and useless at a desk.',
  'confirmations.include': 'Add this one',
  'confirmations.add': 'Add {count} to the trip',
  'confirmations.addOne': 'Add to the trip',
  'confirmations.adding': 'Adding…',
  'confirmations.discard': 'Discard',
  'confirmations.when': 'When',
  'confirmations.ends': 'Ends',
  'confirmations.onClockIn': 'on the clock in {city}',
  'confirmations.noDate': 'No date in this email, so it lands under “not on a day yet”.',
  'confirmations.outsideTrip': 'That date is outside this trip, so it lands under “not on a day yet”.',
  'confirmations.noReference': 'No booking reference found in this email.',
  'notes.title': 'Turn my notes into days',
  'notes.hint': 'Paste your notes — typed, or copied from WhatsApp — and they come back as draft entries. Nothing is added to the trip until you have read them and pressed Add.',
  'notes.field.text': 'Your notes',
  'notes.field.text.guidance': 'Any shape works: bullets, a chat export, a voice-note transcript. Days written as “dia 3” are counted from this trip’s first date. Anything the notes do not say stays blank — addresses, hours and prices are never filled in for you.',
  'notes.placeholder': 'Paste your notes here…',
  'notes.nothingFound': 'Nothing here looks like a plan for the trip. Paste the notes themselves rather than a single line.',
  'notes.foundOne': 'One entry read.',
  'notes.found': '{count} entries read.',
  'notes.unavailable': 'Reading notes is not switched on for this server. Everything else works as usual — add the entries by hand.',
  'notes.failed': 'The reader could not be reached. Try again in a moment, or add the entries by hand.',
  'notes.outsideTrip': 'That date is outside this trip, so it lands under “not on a day yet”.',
  'notes.noDate': 'No date could be settled from your notes, so it lands under “not on a day yet”.',
  'notes.checkHint': 'Read these against your notes before you add them. Dates and times above all — they are what put an entry on the right day.',
  'confirmations.wasRead': 'Read from the email',

  // --- Items: one thing on a day -------------------------------------------
  'item.add': 'Add',
  'item.addFirst': 'Add the first thing',
  'day.editDay': 'Name and note for this day',
  'item.edit': 'Edit',
  'item.editNamed': 'Edit {name}',
  'item.moreActions': 'More for {name}',
  'item.addTitle': 'Add to this day',
  'item.editTitle': 'Edit',
  'item.moreDetails': '+ more details',
  'item.fewerDetails': '− fewer details',
  'item.removeConfirm': 'Remove {name}? The traveller stops seeing it immediately.',
  'item.noTime': 'no set time',
  'item.field.time': 'Time',
  'item.field.time.guidance': 'When it happens, if it happens at a time. Leave it empty for “the Marais, some afternoon” — the day sorts itself, and things with no time sit after the things that have one.',
  'item.field.kind': 'What it is',
  'item.field.kind.guidance': 'Picks the icon the traveller sees and which fields open first. It never limits what you can fill in — a flight can have an address and a restaurant can have a booking reference.',
  'item.field.endTime': 'Ends',
  'item.field.endTimezone': 'Where it ends',
  'item.field.endTimezone.guidance':
    'Only matters when it lands somewhere else. A flight has two clocks — the one it leaves on and the one it arrives on — and the traveller needs the arrival read the way the airport will say it.',
  'item.field.title': 'What to call it',
  'item.field.title.guidance': 'One line, as the traveller should read it: “LATAM 8064 GRU → CDG”, “Dinner at Chez Pippo”, “Airport → hotel transfer”.',
  'item.field.notes': 'Notes',
  'item.field.notes.guidance': 'What she needs to know about this one thing — how to get in, who to ask for, what is already paid. This is where the day’s detail lives now, instead of in one long block at the top.',
  'item.field.address': 'Address',
  'item.field.address.guidance': 'Type the address and the map pin follows — you never enter coordinates. It also becomes a tappable link that opens the traveller’s own maps app.',
  'item.field.addressLocal': 'Address in the local language',
  'item.field.addressLocal.guidance': 'The same address in the local script, to hold up to a driver who does not read Portuguese. On the printed page this is the line that does the most work.',
  'item.field.phone': 'Phone',
  'item.field.phone.guidance': 'Becomes a tap-to-call link. Include the country code, or it will not connect from abroad.',
  'item.field.website': 'Website',
  'item.field.confirmationRef': 'Booking reference',
  'item.field.confirmationRef.guidance': 'The number somebody reads out at a desk. Shown as plain text and never turned into a link, so a policy number is never mistaken for a phone number.',
  'item.field.timezone': 'The time above is on the clock in',
  'item.field.timezone.guidance': 'Almost everything happens where the trip is, so the trip’s city is the usual answer. A flight that crosses zones is the exception — say so here and the traveller sees the departure labelled with the city it leaves from.',
  'item.kind.flight': 'Flight',
  'item.kind.train': 'Train',
  'item.kind.transfer': 'Transfer',
  'item.kind.car': 'Car',
  'item.kind.hotel': 'Hotel',
  'item.kind.restaurant': 'Restaurant',
  'item.kind.cafe': 'Café',
  'item.kind.bar': 'Bar',
  'item.kind.museum': 'Museum',
  'item.kind.activity': 'Activity',
  'item.kind.shop': 'Shop',
  'item.kind.note': 'Note',
  'item.kind.other': 'Place',

  'day.field.title': 'What to call this day',
  'day.field.title.guidance': 'How the day reads in the traveller’s list: “Versalhes”, “Arriving in Paris”, “Happy Birthday, Maria!”. Leave it blank and they see the date.',
  'day.untitled': 'Not named yet',
  'day.notesShort': 'A line about the day',
  'day.notesShort.guidance': 'One sentence above the day’s list — “A free day in the Marais”. Detail belongs on the things themselves now, where the traveller looks for it.',
  'day.empty': 'Nothing on this day yet.',
  'day.itemCountOne': '1 thing',
  'day.itemCount': '{count} things',

  'map.title': 'Where everything is',
  'map.unavailable': 'The map could not be loaded.',
  'map.openIn': 'Open in Maps',

  'error.validation_failed': 'Please check the highlighted fields.',
  'error.unauthorized': 'Please sign in.',
  'error.forbidden': 'You do not have access to that.',
  'error.not_found': 'Not found.',
  'error.conflict': 'That conflicts with something that already exists.',
  'error.rate_limited': 'Too many attempts. Wait a moment and try again.',
  'error.consent_required': 'This information needs a recorded consent before it can be saved.',
  'error.internal': 'Something went wrong on our side.',
  'nav.intake': 'Enquiries',
  'intake.inbox.title': 'Trip enquiries',
  'intake.inbox.hint': 'What each client answered on the form. Nothing here reaches a traveller link.',
  'intake.inbox.empty': 'No enquiries yet. Send the form link after the first WhatsApp conversation.',
  'intake.inbox.linkLabel': 'Form link to send to a client',
  'intake.inbox.copy': 'Copy link',
  'intake.inbox.copied': 'Link copied',
  'intake.inbox.open': 'See what the client sees',
  'intake.inbox.new': 'New',
  'intake.inbox.markHandled': 'Mark as handled',
  'intake.inbox.reopen': 'Reopen',
  'intake.inbox.whatsapp': 'Open in WhatsApp',
  'intake.inbox.travellers': '{adults} adult(s), {children} child(ren)',
  'intake.inbox.lastTrip': 'Last similar trip',
  'intake.inbox.notes': 'Notes',
  'intake.kicker': 'Longhi Travel',
  'intake.title': 'Let\'s design your trip',
  'intake.lead': 'It takes about five minutes. There are no wrong answers: if you don\'t know something, say so. Ana uses what you share to prepare a real conversation, not a generic quote.',
  'intake.step.who': 'Who is travelling',
  'intake.step.trip': 'The trip',
  'intake.step.style': 'Trip style',
  'intake.step.invest': 'Your investment',
  'intake.step.finish': 'To finish',
  'intake.next': 'Continue',
  'intake.back': 'Back',
  'intake.name': 'Your name',
  'intake.whatsapp': 'WhatsApp',
  'intake.whatsapp.hint': 'With country and area code. This is how Ana will reach you.',
  'intake.email': 'Email (optional)',
  'intake.destination': 'Where do you dream of going?',
  'intake.destination.hint': 'A country, a city, or just an idea such as "somewhere in Europe with great food".',
  'intake.when': 'When?',
  'intake.when.hint': 'However you would say it: "July 2027", "during the school holidays".',
  'intake.duration': 'About how many days?',
  'intake.adults': 'Adults',
  'intake.children': 'Children',
  'intake.childrenAges': 'Children\'s ages',
  'intake.childrenAges.hint': 'e.g. 4 and 9',
  'intake.comfort.legend': 'How do you picture the comfort level?',
  'intake.comfort.hint': 'Pick what fits you best. It can be adjusted later.',
  'intake.comfort.essential.title': 'Essential',
  'intake.comfort.essential.desc': 'Good value. Simple, well-located hotels, local restaurants, focused on living the destination.',
  'intake.comfort.comfortable.title': 'Comfortable',
  'intake.comfort.comfortable.desc': '4-star or boutique hotels, a mix of casual restaurants and a few special tables, guided tours when they are worth it.',
  'intake.comfort.premium.title': 'Premium',
  'intake.comfort.premium.desc': '5-star or design hotels, private transfers, experiences you would not find alone.',
  'intake.comfort.luxury.title': 'No compromises',
  'intake.comfort.luxury.desc': 'The best hotels and suites, exclusive access, tailored service end to end.',
  'intake.invest.lead': 'Nobody is expected to know what a trip costs. That is why we do not ask for a budget: Ana builds the proposal after researching and talks numbers with you. These three questions just help pick a starting point.',
  'intake.lastTrip': 'What was the last similar trip you took?',
  'intake.lastTrip.hint': 'Destination, how many days and, if you remember, roughly what you spent. Approximate is fine.',
  'intake.band.legend': 'Per person, not counting flights, what feels comfortable?',
  'intake.band.upto15': 'Up to R$ 15k',
  'intake.band.from15to30': 'R$ 15k to 30k',
  'intake.band.from30to60': 'R$ 30k to 60k',
  'intake.band.above60': 'Above R$ 60k',
  'intake.band.unsure': 'Not sure yet, I\'d rather Ana guides me',
  'intake.interests.legend': 'What must not be missing?',
  'intake.interest.gastronomy': 'Food',
  'intake.interest.culture': 'Culture and history',
  'intake.interest.nature': 'Nature',
  'intake.interest.beach': 'Beach and rest',
  'intake.interest.shopping': 'Shopping',
  'intake.interest.adventure': 'Adventure',
  'intake.interest.wellness': 'Wellness',
  'intake.interest.family': 'Family moments',
  'intake.interest.celebration': 'A special celebration',
  'intake.notes': 'Anything else Ana should know?',
  'intake.notes.hint': 'Pace, places you already know, wishes. Please do not put health or document details here; Ana asks for those later, carefully.',
  'intake.source.legend': 'How did you find Ana?',
  'intake.source.instagram': 'Instagram',
  'intake.source.ads': 'An ad',
  'intake.source.referral': 'A referral',
  'intake.source.whatsapp': 'A WhatsApp chat',
  'intake.source.other': 'Other',
  'intake.consent': 'I agree that Longhi Travel may use these answers to contact me and prepare my trip. I can ask for deletion at any time.',
  'intake.submit': 'Send to Ana',
  'intake.sending': 'Sending…',
  'intake.done.title': 'Received, thank you!',
  'intake.done.body': 'Ana will read everything carefully and reply on your WhatsApp. To get ahead, pick a time for a quick call.',
  'intake.done.bookTitle': 'Pick a time to talk',
  'intake.done.noBooking': 'Ana will be in touch on WhatsApp shortly.',
  'intake.notFound': 'This link is not available. Ask Ana to resend it.',
} as const

export type MessageKey = keyof typeof en
export type Messages = Record<MessageKey, string>
